import React from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { Box, Typography, Avatar } from '@mui/material';
import { tokens, type } from '../theme';
import CompetitionChip from './CompetitionChip';
import { formatWeather, describeWeather } from '../utils/weather';
import { formatMiles } from '../utils/distance';
import { shootoutSuffix, shootoutTitle } from '../utils/shootout';

// The question a parent opens the app to answer: when and where is the next game,
// or how did the last one go. Previously this required landing, waiting, then either
// scrolling or tapping a floating button whose only job was to scroll here — which
// is what prompted surfacing it directly instead.
const NextMatchCard = ({
  game, isMobile: isMobileProp, weather = null, distanceMiles = null,
}) => {
// Layout follows the viewport, not a prop. Callers used to have to pass isMobile
  // and My Team simply did not, so the card silently laid itself out for desktop on
  // a phone — 24px margins where every card around it had 12px. A prop is still
  // honoured when given, so existing callers behave exactly as before, but
  // forgetting it can no longer produce wrong spacing.
  const autoMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const isMobile = isMobileProp !== undefined ? isMobileProp : autoMobile;
  if (!game) return null;

  const date = new Date(game.date);
  const hasScore = game.home_score !== 'TBD'
    && game.home_score !== null
    && game.home_score !== undefined;
  const isPast = date.getTime() < Date.now();
  const played = hasScore;

  const dayLabel = (() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    const days = Math.round((d - today) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days === -1) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  })();

  // Mirrored like the fixture rows below: home reads logo-then-name from the left,
  // away reads name-then-logo to the right edge. Previously both sides rendered
  // left-to-right, so the away team sat against the score instead of the far edge.
  const side = (name, logo, align = 'left') => {
    const label = (
      <Typography sx={{
        fontSize: type.body, fontWeight: 600, color: '#FFFFFF',
        textAlign: align === 'right' ? 'right' : 'left',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden', minWidth: 0,
      }}>{name}</Typography>
    );
    const crest = logo
      ? <Avatar src={logo} alt={name} sx={{ width: 34, height: 34, flexShrink: 0 }} />
      : null;
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      }}>
        {align === 'right' ? <>{label}{crest}</> : <>{crest}{label}</>}
      </Box>
    );
  };

  return (
    <Box sx={{
      backgroundColor: tokens.navy,
      color: '#FFFFFF',
      // Exactly the box the fixture cards below occupy: the same outer gutter as
      // the sections that hold them, and the same inner padding as a row. It
      // previously carried the combined gutter as padding with no margin, so the
      // crests lined up but the navy edge ran wider than every card under it.
      mx: isMobile ? 1.5 : 3,
      px: isMobile ? 1.5 : 2,
      py: isMobile ? 2 : 2.5,
      mb: 2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
        <Typography sx={{
          fontSize: type.label, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: 'rgba(255,255,255,0.72)',
        }}>
          {isPast ? (hasScore ? 'Latest result' : 'Last fixture') : 'Next match'}
        </Typography>
        <Typography
          sx={{ fontSize: type.caption, color: 'rgba(255,255,255,0.72)' }}
          title={describeWeather(weather) || undefined}
        >
          {dayLabel}
                {!played && (
            date.getHours() === 0 && date.getMinutes() === 0
              ? ' · time TBC'
              : ` · ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`
          )}
          {/* This card exists to answer "when and where is the next game", and
              what it will be like there is part of that answer — so weather
              belongs on the day line rather than buried below. */}
          {formatWeather(weather) && ` · ${formatWeather(weather)}`}
        </Typography>
        <CompetitionChip
          competitionType={game.competition_type}
          ageGroup={game.age_group}
          onDark
        />
        {/* Only shown before the match: how far away it is stops mattering once
            you have been. */}
        {!isPast && formatMiles(distanceMiles) && (
          <Typography
            component="span"
            sx={{ fontSize: type.caption, color: 'rgba(255,255,255,0.72)' }}
            title={`${formatMiles(distanceMiles)} in a straight line from you`}
          >
            {formatMiles(distanceMiles)}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {side(game.home_team, game.home_logo, 'left')}
        {/* Score and shootout stack, so the centre column stays one flex item and
            the two sides keep their symmetry. Putting the shootout beside the
            score instead pushes the away team off centre. */}
        <Box sx={{ flexShrink: 0, minWidth: 54, textAlign: 'center' }}>
          <Typography sx={{
            textAlign: 'center',
            fontSize: played ? type.title : type.lead,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}>
            {played ? `${game.home_score} : ${game.away_score}` : 'v'}
          </Typography>
          {shootoutSuffix(game) && (
            <Typography
              title={shootoutTitle(game)}
              sx={{
                fontSize: type.label, whiteSpace: 'nowrap', lineHeight: 1.2,
                color: 'rgba(255,255,255,0.72)',
              }}
            >{shootoutSuffix(game)}</Typography>
          )}
        </Box>
        {side(game.away_team, game.away_logo, 'right')}
      </Box>

      {/* The ground goes to its venue page, exactly as it does on a fixture card.
          This is the card that answers "where is the next game", so it is the one
          place the ground is most worth reaching: every fixture there that day
          across age groups, the weather, and the walk from the car. It rendered
          as plain text here while the same string was a link everywhere else.

          Falls back to Maps on the feed's free text when the ground has no page,
          so an unlocated venue still does something rather than looking broken. */}
      {game.location && game.location.trim() !== '-' && (
        <Typography
          {...(game.venue?.id
            ? { component: Link, to: `/venue/${game.venue.id}` }
            : {
                component: 'a',
                href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.location)}`,
                target: '_blank',
                rel: 'noopener noreferrer',
              })}
          title={game.location}
          sx={{
            display: 'block',
            fontSize: type.caption, color: 'rgba(255,255,255,0.72)', mt: 1.25,
            textDecoration: 'none',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            '&:hover': { color: '#fff', textDecoration: 'underline' },
          }}
        >
          {game.location}
        </Typography>
      )}
    </Box>
  );
};

export default NextMatchCard;
